import React, { useEffect, useRef, useState } from 'react';
import { twMerge } from 'utilities/src/twMerge';
import { NewMenuItem } from '../../data/MenuItem';
import addUrlHostName from '../../utils/addUrlHostname';
import IconComponent from '../Icon';
import MegaMenu from './MegaMenu';

type FlyoutType = {
  menuItems: NewMenuItem[];
  hasLeftText?: boolean;
  bgHeader?: 'Dark' | 'Light' | 'Transparent';
  hasScrolled?: boolean;
};

function Flyout({ menuItems, hasLeftText = false, bgHeader = 'Light', hasScrolled = false }: FlyoutType) {
  const [menuItemIdToShowMegaMenu, setMenuItemIdToShowMegaMenu] = useState('');
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = (menuItemId: string) => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setMenuItemIdToShowMegaMenu(menuItemId);
  };

  const handleMouseLeave = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setMenuItemIdToShowMegaMenu('');
      hideTimeoutRef.current = null;
    }, 200);
  };

  useEffect(
    () => () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    },
    [],
  );

  return (
    <ul className={twMerge('z-40 flex h-full items-center space-x-2')}>
      {menuItems.map((item) => {
        const shouldKeepVisible = menuItemIdToShowMegaMenu === item.id;

        return (
          <li
            key={item.id}
            onMouseEnter={() => handleMouseEnter(item.id)}
            onMouseLeave={handleMouseLeave}
            className={twMerge(
              'group static',
              'flex h-full',
              'py-4 text-base font-bold',
              bgHeader !== 'Transparent' && 'text-site-neutrals-800',
              bgHeader === 'Transparent' && (hasScrolled ? 'text-site-neutrals-800' : 'text-site-neutrals-25'),
              shouldKeepVisible && 'text-site-primary-600',
            )}
          >
            <a
              href={addUrlHostName(`${item.url}` || '')}
              className={twMerge(
                'flex h-10 w-full translate-y-1 items-center justify-center text-nowrap focus:outline-none [&_svg]:ml-2',
                'lgS:px-4',
                'after:bg-site-primary-200 after:absolute after:-bottom-5 after:left-0 after:h-0 after:w-full after:transition-all after:content-[""]',
                shouldKeepVisible && 'after:h-[6px]',
                hasLeftText ? 'px-2' : 'px-4',
              )}
            >
              {item.title}
              {item.sections.length > 0 && (
                <IconComponent
                  iconName="ExpandMore"
                  size="IconM"
                  className={twMerge(
                    'ml-1 translate-y-0.5 transition duration-200',
                    bgHeader !== 'Transparent' && 'bg-site-neutrals-800',
                    bgHeader === 'Transparent' && (hasScrolled ? 'bg-site-neutrals-800' : 'bg-site-neutrals-25'),
                    'group-hover:bg-site-primary-600 group-hover:rotate-180',
                  )}
                />
              )}
            </a>
            {item.sections.length > 0 && (
              <>
                <div
                  className={twMerge(
                    'bg-site-neutrals-900/40 pointer-events-none invisible fixed inset-0 top-20 z-20 h-[100vh] w-full backdrop-blur-[1px]',
                    shouldKeepVisible && 'visible',
                  )}
                />
                <MegaMenu subMenuItem={item} shouldKeepVisible={shouldKeepVisible} />
              </>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export { type FlyoutType };

export default Flyout;
